<script setup lang="ts">
import { IconDownload } from '@tabler/icons-vue';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';

import usePermission from '~/hooks/usePermission';

import useDownloadDDC from '../queries/formalization/useDownloadDDC';

interface Props {
  financingId: number;
}

const props = defineProps<Props>();

const isDownloadingDDC = ref(false);

const financingIdNumber = computed(() => props.financingId);

/** True quando o usuário tem permissão capital_markets (composable = API de permissão nos componentes) */
const { hasAccess: hasDocuments } = usePermission(['capital_markets:view']);

const { refetch: fetchDDC } = useDownloadDDC(financingIdNumber);

const downloadDDCFile = async () => {
  try {
    isDownloadingDDC.value = true;

    const { data, isError } = await fetchDDC();

    if (isError || !data) {
      throw new Error('Erro ao baixar DDC');
    }

    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DDC_${financingIdNumber.value}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('Documento baixado com sucesso!');
  } catch {
    toast.error('Erro ao baixar documento. Tente novamente.');
  } finally {
    isDownloadingDDC.value = false;
  }
};
</script>

<template>
  <div class="financing-documents">
    <div class="card financing-card">
      <div class="card-body">
        <!-- Sem permissão: mostra estado vazio -->
        <div v-if="!hasDocuments" class="empty">
          <p class="empty-title">Nenhum documento disponível</p>
          <p class="empty-subtitle text-secondary">
            Não há documentos disponíveis para visualização no momento.
          </p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-vcenter">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Descrição</th>
                <th class="w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Documento Descritivo de Crédito</td>
                <td>
                  Relatório detalhado sobre o financiamento. Inclui o saldo devedor,
                  o número de parcelas, a taxa de juros e outras condições importantes do contrato.
                </td>
                <td>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    :disabled="isDownloadingDDC"
                    @click="downloadDDCFile"
                  >
                    <IconDownload :size="16" :stroke-width="2" class="me-1" />
                    {{ isDownloadingDDC ? 'Baixando...' : 'Baixar PDF' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.financing-documents {
  .financing-card {
    border-radius: 18px;
    box-shadow: 0 2px 12px 0 rgba(60, 72, 88, 0.10);
    border: none;
  }
}
</style>
